import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Col, Typography, message, Popover, Input, Select, DatePicker, Modal } from "antd";
import {
  PhoneOutlined,
  LoginOutlined,
  LogoutOutlined,
  CustomerServiceOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  hangupCall,
  submitCallFeedback,
  setPopoverOpen,
  setFeedbackData,
  setAgentDisplayName,
  setUserName,
  fetchDispositions,
  clearBusyStatus,
  checkAgentStatus,
} from "../../../store/audioCall/audioCallSlice";
import axiosInstance from "../../../helpers/AxiosHelper";
import CTIFrame from "./CTIFrame";

const { Text } = Typography;
const { TextArea } = Input;

function HeaderAudioCallButton() {
  const dispatch = useDispatch();
  const {
    callStatus,
    callDuration,
    phoneNumber,
    customerName,
    popoverOpen,
    feedbackData,
    feedbackLoading,
    agentDisplayName,
    userName,
    isConfigured,
    isLoggedIn,
    disposition1Options,
    disposition2Options,
    dispositionsLoading,
    agentBusyOnAnotherDevice,
    busyWithUser,
    remoteCallStatus,
  } = useSelector((state) => state.audioCall);

  const [loading, setLoading] = useState(false);
  const [showCTIFrame, setShowCTIFrame] = useState(() => {
    // Initialize from localStorage to persist across page changes
    const storedCtiUrl = localStorage.getItem("cti_url");
    const ctiToken = localStorage.getItem("cti_token");
    return !!(storedCtiUrl && ctiToken);
  });
  const [ctiUrl, setCtiUrl] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem("cti_url") || "";
  });

  // Check agent login status on mount and set up polling
  useEffect(() => {
    // Initial status check
    dispatch(checkAgentStatus());
    
    // Poll status every 30 seconds
    const statusInterval = setInterval(() => {
      dispatch(checkAgentStatus());
    }, 30000);
    
    return () => clearInterval(statusInterval);
  }, [dispatch]);

  // Update CTI frame visibility based on Redux login status
  useEffect(() => {
    const storedCtiUrl = localStorage.getItem("cti_url");
    const ctiToken = localStorage.getItem("cti_token");
    
    // Only show CTI frame if agent is logged in AND not busy on another device AND has valid credentials
    if (isLoggedIn && !agentBusyOnAnotherDevice && storedCtiUrl && ctiToken) {
      setCtiUrl(storedCtiUrl);
      setShowCTIFrame(true);
    } else if (agentBusyOnAnotherDevice || !isLoggedIn || !storedCtiUrl || !ctiToken) {
      // Hide CTI frame if logged out, busy elsewhere, or missing credentials
      setShowCTIFrame(false);
      if (!isLoggedIn || !ctiToken) {
        // Only clear URL if actually logged out (not just busy on another device)
        setCtiUrl("");
      }
    }
  }, [isLoggedIn, agentBusyOnAnotherDevice]);

  // Fetch dispositions when feedback panel opens
  useEffect(() => {
    if (callStatus === "feedback" && disposition1Options.length === 0) {
      dispatch(fetchDispositions());
    }
  }, [callStatus, disposition1Options.length, dispatch]);

  const isInCall = ["ringing_customer", "connected"].includes(callStatus);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusText = () => {
    switch (callStatus) {
      case "ringing_customer": return "Calling customer...";
      case "connected": return "Connected";
      default: return "";
    }
  };

  const handleAgentLogin = async () => {
    setLoading(true);
    try {
      const deviceId = localStorage.getItem("device_uuid") || "web-" + Date.now();
      const response = await axiosInstance.post("/thirdparty/sampark/login/", {
        device_id: deviceId,
      }, { skipErrorRedirect: true });
      if (response.data?.token) {
        localStorage.setItem("cti_token", response.data.token);
        localStorage.setItem("cti_url", response.data.cti_url);
        if (response.data?.agent_name) {
          dispatch(setAgentDisplayName(response.data.agent_name));
        }
        if (response.data?.user_name) {
          dispatch(setUserName(response.data.user_name));
        }
        setCtiUrl(response.data.cti_url);
        setShowCTIFrame(true);
        
        // Check status after successful login using Redux action
        dispatch(checkAgentStatus());
        
        message.success("Agent logged in successfully");
      }
    }catch (error) {
    console.error("Agent login failed:", error);
    const errorMessage = error?.response?.data?.desc || error?.response?.data?.message || error?.message ||  "Agent login failed";
    message.error(errorMessage);
  }finally {
      setLoading(false);
    }
  };

  const handleAgentLogout = async () => {
    // Close the popover first
    dispatch(setPopoverOpen(false));
    
    // Delay the modal to ensure the popover closes first
    setTimeout(() => {
      Modal.confirm({
        title: (
          <div style={{ fontSize: '18px', fontWeight: 600, color: '#262626' }}>
            ⚠️ Warning: Logout from All Devices
          </div>
        ),
        icon: null,
        content: (
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#595959' }}>
            <p style={{ marginTop: 0, marginBottom: 12 }}>
              You are about to logout from all devices where you are currently logged in.
            </p>
            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#262626', fontSize: '14px' }}>This action will:</strong>
            </div>
            <ul style={{ 
              marginTop: 0, 
              marginBottom: 16, 
              paddingLeft: 20,
              listStyleType: 'disc'
            }}>
              <li style={{ marginBottom: 6 }}>End your session on all devices</li>
              <li style={{ marginBottom: 6 }}>Disconnect any active calls</li>
              <li style={{ marginBottom: 0 }}>Require you to login again on all devices</li>
            </ul>
            <p style={{ 
              marginTop: 16, 
              marginBottom: 0, 
              color: '#ff4d4f', 
              fontWeight: 600,
              fontSize: '14px'
            }}>
              Are you sure you want to continue?
            </p>
          </div>
        ),
        okText: "Yes, Logout",
        okType: "danger",
        cancelText: "Cancel",
        width: 480,
        centered: true,
        onOk: async () => {
          setLoading(true);
          try {
            await axiosInstance.post("/thirdparty/sampark/logout/", {}, { skipErrorRedirect: true });
            localStorage.removeItem("cti_token");
            localStorage.removeItem("cti_url");
            setShowCTIFrame(false);
            setCtiUrl("");
            dispatch(setAgentDisplayName(""));
            dispatch(setUserName(""));
            dispatch(clearBusyStatus());
            
            // Check status after logout using Redux action
            dispatch(checkAgentStatus());
            
            message.success("Successfully logged out from all devices");
          } catch (error) {
            console.error("Agent logout failed:", error);
            const errorMessage = error?.response?.data?.desc || error?.response?.data?.message || error?.message || "Agent logout failed";
            message.error(errorMessage);
          } finally {
            setLoading(false);
          }
        },
      });
    }, 100);
  };

  const handleEndCall = () => {
    dispatch(hangupCall());
  };

  const handleSubmitFeedback = () => {
    dispatch(submitCallFeedback({ phoneNumber, feedbackData }));
  };

  const handleCloseFeedback = () => {
    dispatch(setPopoverOpen(false));
  };

  const cardContent = () => {
    // Show busy status if agent is logged in on another device
    if (agentBusyOnAnotherDevice && isLoggedIn) {
      // Determine message based on remote call status
      const isOnCall = remoteCallStatus === "on_call";
      
      return (
        <div style={{ width: 280, padding: 4 }}>
          <div style={{
            textAlign: "center",
            padding: "16px 12px",
            background: isOnCall ? "#fff7e6" : "#f0f5ff",
            borderRadius: 8,
            border: isOnCall ? "1px solid #ffd591" : "1px solid #adc6ff",
            marginBottom: 12,
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: "50%",
              background: isOnCall ? "#ffa940" : "#597ef7",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <PhoneOutlined style={{ fontSize: 22, color: "#fff" }} />
            </div>
            <Text strong style={{ fontSize: 14, display: "block", marginBottom: 4 }}>
              {isOnCall ? "Agent is Busy" : "Agent Logged In"}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              {isOnCall 
                ? "logged in on another device and currently on a call"
                : "logged in on another device"
              }
              {isOnCall && busyWithUser && (
                <span style={{ display: "block", marginTop: 4, fontWeight: 600, color: "#fa8c16" }}>
                  with {busyWithUser}
                </span>
              )}
            </Text>
          </div>
          
          <div style={{
            padding: "10px 12px",
            background: "#f6ffed",
            borderRadius: 6,
            marginBottom: 12,
            border: "1px solid #b7eb8f",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <UserOutlined style={{ fontSize: 16, color: "#52c41a" }} />
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Agent Name</Text>
                <Text strong style={{ fontSize: 13, display: "block" }}>{agentDisplayName}</Text>
                {userName && (
                  <>
                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>User Name</Text>
                    <Text strong style={{ fontSize: 13 }}>{userName}</Text>
                  </>
                )}
              </div>
            </div>
          </div>

          <Button
            block
            danger
            icon={<LogoutOutlined />}
            onClick={handleAgentLogout}
            loading={loading}
          >
            Logout from All Devices
          </Button>
        </div>
      );
    }

    if (!isLoggedIn) {
      return (
        <div style={{ width: 260, padding: 4 }}>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: "50%",
              background: "#f0f5ff",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <CustomerServiceOutlined style={{ fontSize: 22, color: "#1890ff" }} />
            </div>
            <Text strong style={{ fontSize: 14, display: "block" }}>Agent Login</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>Login to start making calls</Text>
          </div>
          <Button
            type="primary"
            block
            icon={<LoginOutlined />}
            onClick={handleAgentLogin}
            loading={loading}
          >
            {loading ? "Connecting..." : "Login"}
          </Button>
        </div>
      );
    }

    if (callStatus === "ringing_customer") {
      return (
        <div style={{ width: 280, padding: 4 }}>
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "#e6f7ff",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <PhoneOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            </div>
            {customerName && <Text strong style={{ fontSize: 14, display: "block" }}>{customerName}</Text>}
            <Text style={{ fontSize: 13, display: "block", marginTop: 2 }}>{phoneNumber}</Text>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>{getStatusText()}</Text>
          </div>
          <Button
            type="primary"
            danger
            block
            icon={<PhoneOutlined style={{ transform: "rotate(135deg)" }} />}
            onClick={handleEndCall}
            style={{ marginTop: 12 }}
          >
            End Call
          </Button>
        </div>
      );
    }

    if (callStatus === "connected") {
      return (
        <div style={{ width: 280, padding: 4 }}>
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "#f6ffed",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
              border: "2px solid #52c41a",
            }}>
              <PhoneOutlined style={{ fontSize: 24, color: "#52c41a" }} />
            </div>
            {customerName && <Text strong style={{ fontSize: 14, display: "block" }}>{customerName}</Text>}
            <Text style={{ fontSize: 13, display: "block", marginTop: 2 }}>{phoneNumber}</Text>
            <Text style={{ color: "#52c41a", fontSize: 12, display: "block", marginTop: 4 }}>Connected</Text>
            <div style={{ marginTop: 6, fontSize: 20, fontWeight: 600, fontFamily: "monospace" }}>
              {formatDuration(callDuration)}
            </div>
          </div>
          <Button
            type="primary"
            danger
            block
            icon={<PhoneOutlined style={{ transform: "rotate(135deg)" }} />}
            onClick={handleEndCall}
            style={{ marginTop: 8 }}
          >
            End Call
          </Button>
        </div>
      );
    }

    if (callStatus === "feedback") {
      return (
        <div style={{ width: 300, padding: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%",
                background: "#fffbe6",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 8px",
              }}>
                <CheckCircleOutlined style={{ fontSize: 16, color: "#faad14" }} />
              </div>
              <Text strong style={{ fontSize: 14, display: "block" }}>Call Feedback</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {customerName ? `${customerName} • ${phoneNumber}` : phoneNumber}
              </Text>
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleCloseFeedback}
              style={{ position: "absolute", top: 8, right: 8 }}
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <Text style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>Disposition 1</Text>
            <Select
              placeholder="Select disposition"
              style={{ width: "100%" }}
              size="small"
              value={feedbackData.disposition_1_id || undefined}
              onChange={(val) => dispatch(setFeedbackData({ ...feedbackData, disposition_1_id: val }))}
              loading={dispositionsLoading}
              disabled={dispositionsLoading}
            >
              {disposition1Options.map((option) => (
                <Select.Option key={option.id} value={option.id}>
                  {option.display_name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 10 }}>
            <Text style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>Disposition 2</Text>
            <Select
              placeholder="Select disposition"
              style={{ width: "100%" }}
              size="small"
              value={feedbackData.disposition_2_id || undefined}
              onChange={(val) => dispatch(setFeedbackData({ ...feedbackData, disposition_2_id: val }))}
              loading={dispositionsLoading}
              disabled={dispositionsLoading}
            >
              {disposition2Options.map((option) => (
                <Select.Option key={option.id} value={option.id}>
                  {option.display_name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: 10 }}>
            <Text style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>Disposition 3</Text>
            <Input
              placeholder="Enter disposition"
              size="small"
              value={feedbackData.disposition3}
              onChange={(e) => dispatch(setFeedbackData({ ...feedbackData, disposition3: e.target.value }))}
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <Text style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>Callback Date</Text>
            <DatePicker
              style={{ width: "100%" }}
              size="small"
              placeholder="Select date"
              onChange={(_, dateString) => dispatch(setFeedbackData({ ...feedbackData, callback_date: dateString }))}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>Call Notes</Text>
            <TextArea
              placeholder="Enter call notes..."
              rows={2}
              value={feedbackData.callnotes}
              onChange={(e) => dispatch(setFeedbackData({ ...feedbackData, callnotes: e.target.value }))}
            />
          </div>

          <Button
            type="primary"
            block
            loading={feedbackLoading}
            onClick={handleSubmitFeedback}
            disabled={!feedbackData.disposition_1_id || !feedbackData.disposition_2_id}
          >
            Submit
          </Button>
        </div>
      );
    }

    // Idle
    return (
      <div style={{ width: 240, padding: 4 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: 10,
          background: "#f6ffed",
          borderRadius: 8,
          border: "1px solid #b7eb8f",
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "#d9f7be",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <UserOutlined style={{ fontSize: 16, color: "#389e0d" }} />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 11, display: "block" }}>Agent Name</Text>
            <Text strong style={{ fontSize: 14, display: "block" }}>{agentDisplayName}</Text>
            {userName && (
              <>
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>User Name</Text>
                <Text strong style={{ fontSize: 13, display: "block" }}>{userName}</Text>
              </>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
              <div style={{
                width: 6, height: 6, borderRadius: "50%",
                backgroundColor: "#52c41a",
              }} />
              <Text type="secondary" style={{ fontSize: 11 }}>Online</Text>
            </div>
          </div>
        </div>
        <Button
          block
          danger
          icon={<LogoutOutlined />}
          onClick={handleAgentLogout}
          loading={loading}
          style={{ marginTop: 12 }}
        >
          Logout
        </Button>
      </div>
    );
  };

  // Inline call indicator — shown when popover is closed but call is active
  const renderCallIndicator = () => {
    if (popoverOpen || !isInCall) return null;

    return (
      <Col className="hp-d-flex-center hp-mr-8">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 10px",
            borderRadius: 16,
            background: callStatus === "connected" ? "#52c41a" : "#1890ff",
            color: "#fff",
            cursor: "pointer",
            height: 30,
          }}
          onClick={() => dispatch(setPopoverOpen(true))}
        >
          <PhoneOutlined style={{ fontSize: 11 }} />
          <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "monospace", lineHeight: 1 }}>
            {callStatus === "connected" ? formatDuration(callDuration) : "Calling..."}
          </span>
          <div
            onClick={(e) => { e.stopPropagation(); handleEndCall(); }}
            style={{
              backgroundColor: "#ff4d4f",
              borderRadius: "50%",
              width: 20, height: 20,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <PhoneOutlined style={{ fontSize: 9, color: "#fff", transform: "rotate(135deg)" }} />
          </div>
        </div>
      </Col>
    );
  };

  return (
    <>
      {renderCallIndicator()}

      {isConfigured && (
        <Col className="hp-d-flex-center hp-mr-4">
          <Popover
            content={cardContent()}
            trigger="click"
            placement="bottomRight"
            open={popoverOpen}
            onOpenChange={(v) => {
              dispatch(setPopoverOpen(v));
            }}
          >
            <Button
              type="text"
              icon={
                <CustomerServiceOutlined
                  style={{
                    fontSize: "20px",
                    color: agentBusyOnAnotherDevice
                      ? "#fa8c16" // Orange for busy on another device
                      : isLoggedIn
                      ? isInCall ? "#1890ff" : "#52c41a"
                      : "#b2bec3",
                  }}
                />
              }
            />
          </Popover>
        </Col>
      )}

      {showCTIFrame && ctiUrl && (
        <CTIFrame
          ctiUrl={ctiUrl}
          onClose={() => setShowCTIFrame(false)}
        />
      )}
    </>
  );
}

export default HeaderAudioCallButton;
